import { getFeedback } from '@sentry/react';
import { Link } from '@tanstack/react-router';
import { PRIVACY_ROUTE, TERMS_ROUTE } from '../../constants/routes';
import { useApiStatusStore } from '../../hooks/useApiStatusStore';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { cn } from '../../lib/classNameHelpers';
import { Card } from '../Card';
import { Button } from '../catalyst-ui-kit/button';
import { StatusIndicator } from '../StatusIndicator/StatusIndicator';

const COMMUNITY_LINKS = [
  {
    icon: '/community-icons/X.svg',
    href: 'https://x.com/etherealdex',
  },
  {
    icon: '/community-icons/Discord.svg',
    href: 'https://discord.com/invite/etherealdex',
  },
  {
    icon: '/community-icons/Github.svg',
    href: 'https://github.com/etherealdex',
  },
  {
    icon: '/community-icons/Mirror.svg',
    href: 'https://mirror.xyz/0x71331A0991C312fcCF766c3Ca8F7a11c4f5F756B',
  },
];

const RESOURCES_LINKS = [
  {
    label: 'Docs',
    href: 'https://docs.ethereal.trade',
  },
];

const LEGAL_LINKS = [
  {
    label: 'Terms',
    href: TERMS_ROUTE,
  },
  {
    label: 'Privacy',
    href: PRIVACY_ROUTE,
  },
];

// Reusable link component with consistent styling
const FooterLink = ({
  href = '#',
  label,
  className,
}: {
  href: string | undefined;
  label: string;
  className?: string;
}) => (
  <Link
    to={href}
    className={cn(
      'whitespace-nowrap py-1.5 md:text-xs text-sm text-contentSecondary transition-colors hover:text-contentPrimary ',
      className
    )}
    target="_blank"
    rel="noopener noreferrer"
  >
    {label}
  </Link>
);

export const Footer = () => {
  const { status, rateLimitedUntil } = useApiStatusStore();
  const feedback = getFeedback();
  const isMd = useTailwindBreakpoint('md');

  return (
    <Card as="footer" className="flex w-full flex-wrap items-center justify-between px-4 md:py-1">
      {isMd && (
        <div className="flex gap-4">
          <StatusIndicator status={status} rateLimitedUntil={rateLimitedUntil} />
          {feedback && (
            <Button
              text
              size="xs"
              className="font-normal"
              color="secondary"
              onClick={async () => {
                const form = await feedback.createForm();
                form.appendToDom();
                form.open();
              }}
            >
              Report a bug
            </Button>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2 md:flex-row md:gap-4">
        <div className="flex flex-col gap-1 md:flex-row md:gap-4">
          {RESOURCES_LINKS.map((link) => (
            <FooterLink key={link.label} {...link} />
          ))}
          {LEGAL_LINKS.map((link) => (
            <FooterLink key={link.label} {...link} />
          ))}
        </div>
        <div className="flex gap-3">
          {COMMUNITY_LINKS.map((link) => (
            <a
              key={link.icon}
              href={link.href}
              className="py-1.5"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={link.icon}
                alt={link.icon}
                className="size-6 cursor-pointer hover:brightness-150 md:size-4"
              />
            </a>
          ))}
        </div>
      </div>
    </Card>
  );
};
